import React from "react";
import { usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM_PHOTO = "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1400";

export default function AboutUs() {
  usePageMeta(
    "Chi Siamo — Sdebitiamoci Srl | Consulenti Saldo e Stralcio",
    "Sdebitiamoci Srl: consulenti specializzati nel saldo e stralcio e nella riduzione dei debiti. Consulenza gratuita, compenso solo al successo."
  );
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      {/* Intro */}
      <section className="container-editorial">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Chi siamo</p>
        <h1 className="font-serif text-5xl md:text-[5.5rem] leading-[0.88] mt-6 text-[#2d6fc4] max-w-5xl">
          Un <em className="italic text-[#2d6fc4]">team</em> che siede dall'altra parte del tavolo, per te.
        </h1>
        <p className="mt-8 text-lg text-[#333333] leading-relaxed max-w-3xl">
          Sdebitiamoci Srl è uno studio italiano di consulenti, analisti e negoziatori. Il fondatore vanta oltre vent'anni di esperienza nel settore del saldo e stralcio e nella gestione dei debiti. Lavoriamo in team perché ogni pratica richiede più competenze: analisi documentale, negoziazione con i creditori, coordinamento legale e vicinanza umana al cliente.
        </p>
      </section>

      {/* Single team photo */}
      <section className="container-editorial mt-16" data-testid="team-photo-section">
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-80px" }}
          className="relative overflow-hidden rounded-lg border border-[#E5E7EB] shadow-xl"
        >
          <div className="aspect-[16/9] w-full bg-[#F1F5F9]">
            <img
              src={TEAM_PHOTO}
              alt="Il team di Sdebitiamoci Srl"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Overlay caption */}
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-[#0B1F3F]/90 via-[#0B1F3F]/60 to-transparent">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">— Il team Sdebitiamoci</p>
                <p className="font-serif text-3xl md:text-4xl text-white mt-2 leading-tight max-w-xl">
                  Consulenti, analisti e negoziatori — tutti al servizio del tuo caso.
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-[#14417B] animate-pulse"/>
                <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-white">Operativi in tutta Italia</span>
              </div>
            </div>
          </div>
        </motion.figure>
        <p className="mt-4 text-xs font-mono text-[#333333]">Sdebitiamoci Srl — Italia</p>
      </section>

      {/* Manifesto */}
      <section className="container-editorial mt-24 border-t border-[#E5E7EB] pt-16">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Manifesto</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#2d6fc4]">Cosa crediamo.</h2>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-8">
            {[
              ["I debiti si negoziano.", "Nessun importo iscritto a ruolo è sacro. Quasi sempre c'è margine di riduzione, se si conosce il linguaggio giusto."],
              ["Le persone vengono prima delle pratiche.", "Ogni caso è un cognome, non un codice. Se non c'è fiducia reciproca, non c'è metodo che tenga."],
              ["Il compenso arriva dopo il risultato.", "È l'unico modo per essere davvero allineati con chi ci sceglie."],
              ["Salvare la casa è civile prima che tecnico.", "Un'asta immobiliare rovina la vita per dieci anni. Prevenirla è, spesso, matematicamente possibile."],
            ].map(([t, d]) => (
              <div key={t}>
                <h3 className="font-serif text-2xl md:text-3xl text-[#2d6fc4]">{t}</h3>
                <p className="mt-2 text-[#333333] max-w-2xl leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-editorial mt-24">
        <div className="bg-[#F1F5F9] border border-[#E5E7EB] rounded-lg p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <p className="font-serif text-3xl md:text-4xl max-w-xl leading-tight text-[#2d6fc4]">
            Vuoi capire se il tuo caso è trattabile? <span className="italic text-[#2d6fc4]">Contattaci per una consulenza conoscitiva.</span>
          </p>
          <Link to="/contatti" data-testid="team-cta" className="btn-cta inline-flex items-center gap-2 px-7 py-4 rounded-lg text-sm">
            Consulenza gratuita <ArrowUpRight size={16}/>
          </Link>
        </div>
      </section>
    </div>
  );
}
